'use client';

import { ReactNode, Children, isValidElement } from 'react';

interface ColumnProps {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
}

export function Column({ children }: ColumnProps) {
  return <>{children}</>;
}

interface ColumnsProps {
  children: ReactNode;
  columns?: 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  align?: 'left' | 'center' | 'right';
}

export function Columns({ children, columns = 2, gap = 'md', align = 'left' }: ColumnsProps) {
  const columnItems = Children.toArray(children).filter((child) =>
    isValidElement(child) && child.type === Column
  );

  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
  };

  const columnClasses = {
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
  };

  const getAlignmentClasses = (columnAlign?: 'left' | 'center' | 'right') => {
    const effectiveAlign = columnAlign || align;
    const alignClasses = {
      left: 'sm:text-left',
      center: 'sm:text-center',
      right: 'sm:text-right',
    };
    return `text-center ${alignClasses[effectiveAlign]}`;
  };

  return (
    <div className={`my-8 grid grid-cols-1 sm:grid-cols-2 ${columnClasses[columns]} ${gapClasses[gap]}`}>
      {columnItems.map((column, index) => {
        if (!isValidElement<ColumnProps>(column)) return null;
        const columnAlign = column.props.align;
        const alignmentClasses = getAlignmentClasses(columnAlign);

        return (
          <div key={index} className={alignmentClasses}>
            {column.props.children}
          </div>
        );
      })}
    </div>
  );
}
